/**
 * CMS-13 "Sesiones" (docs/06 §2.12): the list on the left, the detail on the right — host,
 * device, window, join code, the feature snapshot the guests inherit, every participant (the ones
 * who left included) and the full `session_songs` history with the removed rows.
 *
 * "Cerrar sesión" calls `POST /cms/sessions/:id/close`, which reuses WP1.5's `closeSession`, so
 * D1, the KV status cache and RTDB are updated together.
 */
import type {
  CmsSessionDetailResponse,
  CmsSessionDTO,
  CmsSessionsQueryInput,
  CmsSessionsResponse,
  SessionStatus,
} from '@akpl/shared';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';

import { api } from '../api';
import { atLeast, useAuth } from '../auth/AuthProvider';
import { SessionSongStatusBadge, SessionStatusBadge } from '../components/access/badges';
import { ExportCsvButton } from '../components/data-table/ExportCsvButton';
import { ListError } from '../components/data-table/ListError';
import { PageHeader } from '../components/layout/AppShell';
import { resultsAnchorProps, useResultsAnchor } from '../components/layout/resultsAnchor';
import { Button } from '../components/ui/button';
import { useConfirm } from '../components/ui/confirm';
import { Input, Select } from '../components/ui/field';
import { Badge, Panel, PanelHeader } from '../components/ui/panel';
import { useT } from '../i18n';
import { cn } from '../lib/cn';
import type { CsvColumn } from '../lib/csv';
import { fetchAllRowsResult } from '../lib/exportAll';
import { formatDateTime, formatNumber, formatStamp } from '../lib/format';

export interface SessionFilters {
  q: string;
  status: SessionStatus | '';
}

export const EMPTY_SESSION_FILTERS: SessionFilters = { q: '', status: '' };

export function toSessionsQuery(filters: SessionFilters, page = 1): CmsSessionsQueryInput {
  const query: CmsSessionsQueryInput = { page, pageSize: 50, sort: 'started_at', dir: 'desc' };
  if (filters.q.trim()) query.q = filters.q.trim();
  if (filters.status) query.status = filters.status;
  return query;
}

/** CMS-21: the operator can take the session list out of the console (docs/06 §4). */
export const sessionCsvColumns: CsvColumn<CmsSessionDTO>[] = [
  { header: 'id', value: (s) => s.id },
  { header: 'join_code', value: (s) => s.joinCode },
  { header: 'status', value: (s) => s.status },
  { header: 'host_uid', value: (s) => s.hostUid },
  { header: 'host_name', value: (s) => s.hostName },
  { header: 'host_email', value: (s) => s.hostEmail },
  { header: 'device_id', value: (s) => s.deviceId },
  { header: 'device_model', value: (s) => s.deviceModel },
  { header: 'device_platform', value: (s) => s.devicePlatform },
  { header: 'participants', value: (s) => s.participantCount },
  { header: 'songs_played', value: (s) => s.songsPlayed },
  { header: 'started_at', value: (s) => s.startedAt },
  { header: 'expires_at', value: (s) => s.expiresAt },
  { header: 'closed_at', value: (s) => s.closedAt },
  { header: 'closed_reason', value: (s) => s.closedReason },
];

function SessionRow({
  session,
  selected,
  onSelect,
}: {
  session: CmsSessionDTO;
  selected: boolean;
  onSelect: () => void;
}) {
  const { t, i18n } = useT();
  return (
    <li>
      <button
        type="button"
        onClick={onSelect}
        aria-current={selected}
        className={cn(
          'w-full border-b border-line px-4 py-2.5 text-left last:border-0',
          selected ? 'bg-pink/6' : 'hover:bg-canvas',
        )}
      >
        <span className="flex items-center gap-2">
          <span className="text-[14px] font-bold text-ink">{session.joinCode}</span>
          <SessionStatusBadge status={session.status} />
        </span>
        <span className="mt-0.5 block truncate text-[12px] text-muted">
          {session.hostName} · {session.deviceModel ?? session.deviceId}
        </span>
        <span className="block text-[12px] text-muted">
          {t('cms:sessions.since', { time: formatStamp(session.startedAt, i18n.language) })} ·{' '}
          {t('cms:sessions.songsShort', { count: session.songsPlayed })}
        </span>
      </button>
    </li>
  );
}

function SessionDetail({ sid }: { sid: string }) {
  const { t, i18n } = useT();
  const locale = i18n.language;
  const { role } = useAuth();
  const canClose = atLeast(role, 'editor');
  const queryClient = useQueryClient();
  const [error, setError] = useState<string | null>(null);

  const detail = useQuery<CmsSessionDetailResponse>({
    queryKey: ['cms', 'session', sid],
    queryFn: () => api.cmsSession({ params: { id: sid } }),
  });

  const confirm = useConfirm();

  const close = useMutation({
    mutationFn: () => api.cmsSessionClose({ params: { id: sid }, body: {} }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['cms', 'session', sid] });
      void queryClient.invalidateQueries({ queryKey: ['cms', 'sessions'] });
    },
    onError: () => setError(t('common:states.error')),
  });

  if (detail.isLoading) {
    return <p className="p-6 text-[13px] text-muted">{t('common:states.loading')}</p>;
  }
  if (!detail.data) {
    return <p className="p-6 text-[13px] text-danger">{t('common:states.error')}</p>;
  }

  const { session, participants, history } = detail.data;

  /** Closing a session throws every participant out of the room, so it asks first (UX-211). */
  const onClose = async () => {
    const ok = await confirm({
      title: t('cms:confirm.closeSession.title'),
      body: t('cms:confirm.closeSession.body'),
      confirmLabel: t('cms:confirm.closeSession.confirm'),
      tone: 'danger',
    });
    if (ok) close.mutate();
  };
  const played = history.filter((item) => item.playedAt !== null).length;

  return (
    <div className="flex flex-col gap-4">
      <Panel data-testid="session-summary">
        <PanelHeader
          title={`${t('cms:sessions.title')} ${session.joinCode}`}
          subtitle={t('cms:sessions.meta', { id: session.id, device: session.deviceId })}
          action={
            canClose && session.status === 'active' ? (
              <Button
                variant="danger"
                size="sm"
                disabled={close.isPending}
                onClick={() => void onClose()}
              >
                {t('cms:sessions.closeRoom')}
              </Button>
            ) : (
              <SessionStatusBadge status={session.status} />
            )
          }
        />
        <dl className="grid grid-cols-2 gap-4 px-4 py-3 md:grid-cols-5">
          {(
            [
              ['host', session.hostName],
              ['device', session.deviceModel ?? session.deviceId],
              ['started', formatDateTime(session.startedAt, locale)],
              ['expires', formatDateTime(session.expiresAt, locale)],
              ['code', session.joinCode],
            ] as const
          ).map(([key, value]) => (
            <div key={key}>
              <dt className="text-[12px] font-semibold tracking-wide text-muted uppercase">
                {t(`cms:sessions.${key}` as 'cms:sessions.host')}
              </dt>
              <dd className="mt-0.5 text-[13px] font-semibold text-ink">{value}</dd>
            </div>
          ))}
        </dl>
        <div className="flex flex-wrap items-center gap-2 border-t border-line px-4 py-2.5">
          <span className="text-[12px] text-muted">{t('cms:sessions.snapshot')}</span>
          {session.features.length > 0 ? (
            session.features.map((feature) => (
              <Badge key={feature} tone="cyan">
                {feature}
              </Badge>
            ))
          ) : (
            <span className="text-[12px] text-muted">{t('cms:sessions.noFeatures')}</span>
          )}
        </div>
        {error && (
          <p className="px-4 pb-3 text-[12px] text-danger" role="alert">
            {error}
          </p>
        )}
      </Panel>

      <Panel data-testid="session-participants">
        <PanelHeader
          title={t('cms:sessions.participants.title')}
          subtitle={t('cms:sessions.participants.subtitle', { count: participants.length })}
        />
        {participants.length > 0 ? (
          <ul className="divide-y divide-line">
            {participants.map((participant) => (
              <li key={participant.uid} className="flex items-center gap-3 px-4 py-2.5 text-[13px]">
                <span className="min-w-0 flex-1">
                  <span className="block truncate font-semibold text-ink">{participant.name}</span>
                  <span className="block truncate text-[12px] text-muted">
                    {participant.email ?? t('cms:users.guest')}
                  </span>
                </span>
                <Badge tone={participant.role === 'host' ? 'pink' : 'neutral'}>
                  {participant.role}
                </Badge>
                <span className="text-[12px] text-muted">
                  {participant.leftAt
                    ? t('cms:sessions.participants.left', {
                        time: formatStamp(participant.leftAt, locale),
                      })
                    : t('cms:sessions.participants.joined', {
                        time: formatStamp(participant.joinedAt, locale),
                      })}
                </span>
              </li>
            ))}
          </ul>
        ) : (
          <p className="px-4 py-4 text-[13px] text-muted">{t('cms:sessions.participants.empty')}</p>
        )}
      </Panel>

      <Panel data-testid="session-history">
        <PanelHeader
          title={t('cms:sessions.history.title')}
          subtitle={t('cms:sessions.history.subtitle')}
          action={
            <Badge>{t('cms:sessions.history.count', { played, total: history.length })}</Badge>
          }
        />
        {history.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="w-full border-collapse text-left text-[13px]">
              <thead>
                <tr className="border-b border-line text-[12px] font-semibold tracking-wide text-muted uppercase">
                  <th scope="col" className="px-4 py-2">
                    {t('cms:sessions.history.song')}
                  </th>
                  <th scope="col" className="px-4 py-2">
                    {t('cms:sessions.history.tone')}
                  </th>
                  <th scope="col" className="px-4 py-2">
                    {t('cms:sessions.history.addedBy')}
                  </th>
                  <th scope="col" className="px-4 py-2">
                    {t('cms:sessions.history.via')}
                  </th>
                  <th scope="col" className="px-4 py-2">
                    {t('cms:sessions.history.status')}
                  </th>
                  <th scope="col" className="px-4 py-2">
                    {t('cms:sessions.history.time')}
                  </th>
                </tr>
              </thead>
              <tbody>
                {history.map((item) => (
                  <tr key={item.id} className="border-b border-line last:border-0">
                    <td className="px-4 py-2">
                      <span className="block font-semibold text-ink">{item.title}</span>
                      <span className="block text-[12px] text-muted">{item.artistNames}</span>
                    </td>
                    <td className="px-4 py-2 font-semibold tabular-nums">{item.tone}</td>
                    <td className="px-4 py-2">{item.addedByName}</td>
                    <td className="px-4 py-2 text-muted">{item.addedVia}</td>
                    <td className="px-4 py-2">
                      <SessionSongStatusBadge status={item.status} />
                    </td>
                    <td className="px-4 py-2 text-muted tabular-nums">
                      {item.playedAt
                        ? formatStamp(item.playedAt, locale)
                        : item.startedAt
                          ? formatStamp(item.startedAt, locale)
                          : '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <p className="px-4 py-4 text-[13px] text-muted">{t('cms:sessions.history.empty')}</p>
        )}
      </Panel>
    </div>
  );
}

const SESSION_HOURS = 6;

export function Sessions() {
  // UX-576: /sessions is a list too, so it gets the «Saltar a los resultados» link.
  useResultsAnchor();
  const { t, i18n } = useT();
  const locale = i18n.language;
  const [filters, setFilters] = useState<SessionFilters>(EMPTY_SESSION_FILTERS);
  const [selected, setSelected] = useState<string | null>(null);

  const query = toSessionsQuery(filters);
  const result = useQuery<CmsSessionsResponse>({
    queryKey: ['cms', 'sessions', query],
    queryFn: () => api.cmsSessions({ query }),
    placeholderData: keepPreviousData,
  });

  const items = result.data?.items ?? [];
  /**
   * `?? { active: 0, last30d: 0 }` made the header read "0 activas ahora" and the panel
   * "0 activas · 0 en los últimos 30 días" for the whole first load, next to a dashboard saying
   * "Sesiones activas 40" (QA 2026-09-15 UX-530). Until the query answers the screen says it is
   * loading instead of inventing a zero.
   */
  const counts = result.data?.counts ?? null;

  return (
    <>
      <PageHeader
        title={t('cms:sessions.title')}
        subtitle={
          counts
            ? t('cms:sessions.subtitle', {
                active: formatNumber(counts.active, locale),
                hours: SESSION_HOURS,
              })
            : t('cms:sessions.subtitleLoading', { hours: SESSION_HOURS })
        }
        actions={
          <span className="flex items-center gap-2">
            <ExportCsvButton
              filename="akapella-sessions.csv"
              columns={sessionCsvColumns}
              data-testid="sessions-export"
              // The whole filtered set, not the page on screen (CMS-22).
              rows={(onProgress) =>
                fetchAllRowsResult(
                  (page, pageSize) => api.cmsSessions({ query: { ...query, page, pageSize } }),
                  { onProgress },
                )
              }
            />
            <Select
              className="w-44"
              aria-label={t('cms:sessions.statusAll')}
              value={filters.status}
              onChange={(e) =>
                setFilters((f) => ({ ...f, status: e.target.value as SessionStatus | '' }))
              }
            >
              <option value="">{t('cms:sessions.statusAll')}</option>
              <option value="active">{t('cms:status.active')}</option>
              <option value="closed">{t('cms:status.closed')}</option>
              <option value="expired">{t('cms:status.expired')}</option>
            </Select>
            <Input
              className="w-60"
              placeholder={t('cms:sessions.searchPlaceholder')}
              aria-label={t('cms:sessions.searchPlaceholder')}
              value={filters.q}
              onChange={(e) => setFilters((f) => ({ ...f, q: e.target.value }))}
            />
            {/* UX-514: a way back from a filter combination that returned nothing. */}
            <Button
              variant="link"
              size="sm"
              disabled={filters.q === '' && filters.status === ''}
              onClick={() => setFilters(EMPTY_SESSION_FILTERS)}
            >
              {t('cms:table.clearFilters')}
            </Button>
          </span>
        }
      />

      <div className="grid grid-cols-1 gap-4 px-6 pb-8 lg:grid-cols-[340px_minmax(0,1fr)]">
        <Panel className="self-start" {...resultsAnchorProps(t('cms:sessions.listTitle'))}>
          <PanelHeader
            title={t('cms:sessions.listTitle')}
            subtitle={
              counts
                ? t('cms:sessions.listSubtitle', {
                    active: formatNumber(counts.active, locale),
                    total: formatNumber(counts.last30d, locale),
                  })
                : t('cms:sessions.listSubtitleLoading')
            }
          />
          {result.isError ? (
            <ListError onRetry={() => void result.refetch()} />
          ) : result.isLoading ? (
            <p className="px-4 py-4 text-[13px] text-muted">{t('common:states.loading')}</p>
          ) : items.length === 0 ? (
            <p className="px-4 py-4 text-[13px] text-muted">{t('cms:sessions.empty')}</p>
          ) : (
            <ul>
              {items.map((session) => (
                <SessionRow
                  key={session.id}
                  session={session}
                  selected={selected === session.id}
                  onSelect={() => setSelected(session.id)}
                />
              ))}
            </ul>
          )}
        </Panel>

        {selected ? (
          <SessionDetail key={selected} sid={selected} />
        ) : (
          <Panel className="flex min-h-40 items-center justify-center">
            <p className="text-[13px] text-muted">{t('cms:sessions.pick')}</p>
          </Panel>
        )}
      </div>
    </>
  );
}
